import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

import { AddErrors } from "../../actions/errors";
import { GetODTAndProcessorAvailability, SetDisplayODTModal } from "../../actions/card";
import { appInsights } from "../../services/appInsights";
import { useAppDispatch } from "../../store/hooks";
import { COMMON, PAGES } from "../../constants/localization";
import { ACTION_DISPLAY_TYPES } from "../../constants/enums";
import { ROUTES } from "../../constants/clientRoutes";
import { FEATURE_FLAGS } from "../../constants/featureFlags";
import i18n from "../../i18n/config";

// types
import { RootState } from "../../types/redux";

// ui components
import QuickLinks from "../../components/Dashboard/QuickLinks";
import Alerts from "../../components/Dashboard/Alerts";
import Balance from "../../components/Dashboard/Balance";
import PaymentSummary from "../../components/Dashboard/PaymentSummary";
import Modal from "../../components/Common/Modal";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import ExchangeRateModalUntyped from "../../components/Dashboard/ExchangeRateModal";

const ExchangeRateModal = ExchangeRateModalUntyped as unknown as React.ComponentType<{
  featureFlags: string[];
}>;

const Dashboard = () => {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showProcessorDownModal, setShowProcessorDownModal] = useState<boolean>(false);
  const card = useSelector((state: RootState) => state.card);
  const user = useSelector((state: RootState) => state.user);
  const showODTModal = card.availability.showODTModal === true;
  const initialApiCallErrors = useSelector((state: RootState) => state.initialApiCallErrors);

  useEffect(() => {
    const getProcessorAvailability = async () => {
      await dispatch((GetODTAndProcessorAvailability as any)());
    };

    getProcessorAvailability();
  }, []);

  useEffect(() => {
    if (card.availability.isProcessorUnavailable) {
      setShowProcessorDownModal(true);
    }
  }, [card.availability.isProcessorUnavailable]);

  useEffect(() => {
    if (initialApiCallErrors) {
      dispatch(
        (AddErrors as any)([
          {
            errorType: 4,
            errorTitle: COMMON.GENERIC_ERROR_MODAL_HEADER,
            errorMessage: `${COMMON.INITIALIZE_APP_ERROR} Error Code returned: ${initialApiCallErrors.errorCode}`,
          },
        ])
      );

      appInsights.trackException({
        exception: new Error("App Initial API calls Error"),
        severityLevel: SeverityLevel.Error,
        properties: {
          errorCode: initialApiCallErrors.errorCode,
          apiCallError: initialApiCallErrors.apiCall,
          username: user?.profile?.userName || null,
        },
      });
    }
  }, [initialApiCallErrors]);

  const dismissProcessorDownModal = () => {
    setShowProcessorDownModal(false);
  };

  const dismissODTDownModal = async () => {
    await dispatch((SetDisplayODTModal as any)(false));
  };

  if (user.featureFlags.includes(FEATURE_FLAGS.COMPANION_PARTICIPANT)) {
    location.href = ROUTES.EXTERNAL.MY_CARD;
  }

  return (
    <div id="dashboard" className="v2 v3 spacing-bottom-large">
      <Helmet>
        <title>{i18n.t("PAGES.DASHBOARD.PAGE_TITLE")}</title>
      </Helmet>
      <h1 className="sr-only">{i18n.t("PAGES.DASHBOARD.PAGE_TITLE")}</h1>
      <Alerts directives={card.notifications} featureFlags={user.featureFlags} />

      <Balance card={card} featureFlags={user.featureFlags} />

      <div className="links-payment-wrapper">
        <QuickLinks
          setShowModal={setShowModal}
          featureFlags={user.featureFlags}
          initialApiCallErrors={initialApiCallErrors}
        />

        <PaymentSummary />
      </div>

      <Modal
        onClose={() => setShowModal(false)}
        open={showModal}
        title={"Check Current Exchange Rates"}
        className={"medium"}
        disableNativeActions={true}
        content={<ExchangeRateModal featureFlags={user.featureFlags} />}
      />

      <Modal
        open={showProcessorDownModal}
        title={PAGES.DASHBOARD.PROCESSOR_DOWN_MODAL.TITLE}
        content={PAGES.DASHBOARD.PROCESSOR_DOWN_MODAL.CONTENT}
        onClose={dismissProcessorDownModal}
        actions={[
          {
            title: COMMON.CLOSE,
            onClick: () => dismissProcessorDownModal(),
            displayType: ACTION_DISPLAY_TYPES.PRIMARY,
            focused: true,
          },
        ]}
      />

      <Modal
        open={showODTModal}
        title={PAGES.DASHBOARD.ODT_DOWN_MODAL.TITLE}
        content={PAGES.DASHBOARD.ODT_DOWN_MODAL.CONTENT}
        onClose={dismissODTDownModal}
        actions={[
          {
            title: COMMON.OK,
            onClick: () => dismissODTDownModal(),
            displayType: ACTION_DISPLAY_TYPES.PRIMARY,
            focused: true,
          },
        ]}
      />

      <ApiErrorModal />
    </div>
  );
};

export default Dashboard;